import { FC, useEffect } from "react";
import { useRouter } from "next/router";
import Dialog, {
  DialogContent,
  DialogTitle,
  DialogActions,
} from "@/components/Dialog";
import Button from "@/components/Button";
import Typography from "@/components/Typography";
import styles from "./FamilyMatchVideoDialog.module.scss";
import { useTrackers, useCurrentTrip } from "@/hooks";
import { OrientationVideoPlayer } from "@/components/Arrival";
import { useDetailsTripQuery } from "@/fetch/gworld";
import { useMarkTaskCompleteMutation, useTripTasksQuery } from "@/fetch/tasks";

type Props = {
  onClose: () => void;
};

const FamilyMatchVideoDialog: FC<Props> = ({ onClose }) => {
  const { currentTrip } = useCurrentTrip();
  const { data: tripDetails, isLoading: isLoadingTripDetails } =
    useDetailsTripQuery(currentTrip?.id, {
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    });
  const { mutateAsync: markTaskComplete, isLoading: isCompletingTask } =
    useMarkTaskCompleteMutation();
  const { data: tasksData, isLoading: isLoadingTasks } = useTripTasksQuery();
  const { track, captureException } = useTrackers();

  const familyMatchVideoTask = tasksData?.result?.find(
    (eachTask) => eachTask.task_id === tripDetails?.family_match_video_task_id
  );

  useEffect(() => {
    if (
      Boolean(
        familyMatchVideoTask?.complete ||
          familyMatchVideoTask?.status === "COMPLETE"
      )
    ) {
      onClose();
    }
  }, [familyMatchVideoTask]);

  useEffect(() => {
    if (!tripDetails) return;
    if (
      !Boolean(tripDetails?.family_match_video_signed_url?.["video/original"])
    ) {
      onClose();
    }
  }, [tripDetails]);

  const updateFamilyMatchTask = async () => {
    const familyMatchVideoTaskId = tripDetails?.family_match_video_task_id;
    track("Family Match Video Watched", {
      eventId: "family-match-video-watched",
    });
    if (!familyMatchVideoTaskId || Boolean(familyMatchVideoTask?.complete)) {
      onClose();
      return;
    }
    if (isCompletingTask || isLoadingTasks) return;
    try {
      await markTaskComplete({ taskId: familyMatchVideoTaskId });
      onClose();
    } catch (error) {
      console.error({ error });
      captureException(error);
    }
  };

  const updateVideoProgress = (progress: number) => {
    track("Family Match Video Progress Updated", {
      eventId: "family-match-video-progress-updated",
      progress: progress,
    });
  };

  return (
    <Dialog open>
      <DialogContent className={styles.dialogContent}>
        <Typography variant="h6">Family Match Process</Typography>
        <Typography
          variant="subtitle1"
          color="text.secondary"
          className={styles.description}
        >
          To understand how our family matching process works, please watch this
          video before your Au Pair Program Manager reaches out! Once the video
          is finished, you will then be able to navigate gWorld as normal. You
          can refer back to this video at anytime in the host family section.
        </Typography>
        <div className={styles.videoContainer}>
          {tripDetails?.family_match_video_signed_url && (
            <OrientationVideoPlayer
              orientationVideoUrls={tripDetails?.family_match_video_signed_url}
              onVideoWatched={() => {}}
              onProgressUpdate={updateVideoProgress}
              onVideoCompleted={updateFamilyMatchTask}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FamilyMatchVideoDialog;
